import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, AlertTriangle, Clock, Calculator, TrendingUp, Shield, ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const ExposureCalculatorPage = () => {
  const { language } = useLanguage();
  const [calcIndustry, setCalcIndustry] = useState('');
  const [calcSize, setCalcSize] = useState('');
  const [calcVisitors, setCalcVisitors] = useState('');
  const [calcLocations, setCalcLocations] = useState('');
  const [calcResults, setCalcResults] = useState(null);

  const calculateExposure = () => {
    const size = parseInt(calcSize) || 50000;
    const visitors = parseInt(calcVisitors) || 500;
    const locations = parseInt(calcLocations) || 1;
    const incidentRate = size > 100000 ? 0.08 : size > 50000 ? 0.06 : 0.04;
    const incidents = Math.round(visitors * 365 * incidentRate / 1000 * locations);
    const avgClaim = 35000;
    const claimRate = 0.12;
    const liability = Math.round(incidents * claimRate * avgClaim);
    const disruption = Math.round(incidents * 2.5 * 150);
    setCalcResults({ incidents, liability, disruption });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="exposure-calculator-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-sm font-bold text-[#0077FF] uppercase tracking-widest mb-4 block">Financial Exposure</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-5" data-testid="exposure-page-title">Exposure & Loss Calculator</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">Estimate your organization's annual hazard exposure based on facility characteristics. Model incident frequency, liability costs, and operational disruption.</p>
        </div>
      </section>

      {/* Risk-Based Deployment Models */}
      <section className="py-16 bg-slate-50" data-testid="risk-deployment-models-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-sm font-bold text-blue-600 uppercase tracking-widest mb-4 block">Risk Intelligence</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Risk-Based Deployment Models</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">How Tapelligence determines deployment scale, coverage requirements, and exposure thresholds based on operational risk analysis.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">Risk Coverage Methodology</h3>
              <p className="text-sm text-slate-600 mb-4">Deployment decisions are driven by facility risk profiles, not arbitrary estimates. Each environment is assessed against industry-specific incident benchmarks to determine minimum coverage requirements.</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-slate-600"><span className="text-blue-500 mt-0.5">&#x2022;</span>Incident frequency per 10,000 sq ft</li>
                <li className="flex items-start gap-2 text-sm text-slate-600"><span className="text-blue-500 mt-0.5">&#x2022;</span>Traffic density and peak-hour modeling</li>
                <li className="flex items-start gap-2 text-sm text-slate-600"><span className="text-blue-500 mt-0.5">&#x2022;</span>Zone classification (high / medium / low risk)</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="w-5 h-5 text-[#FF7F00]" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">Exposure Modeling Logic</h3>
              <p className="text-sm text-slate-600 mb-4">Financial exposure is calculated by combining incident probability with average claim severity and operational disruption costs specific to each industry vertical.</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-slate-600"><span className="text-red-500 mt-0.5">&#x2022;</span>Average claim cost: $35,000 per incident</li>
                <li className="flex items-start gap-2 text-sm text-slate-600"><span className="text-red-500 mt-0.5">&#x2022;</span>Litigation rate: 12% of reported incidents</li>
                <li className="flex items-start gap-2 text-sm text-slate-600"><span className="text-red-500 mt-0.5">&#x2022;</span>Disruption cost: $375 per incident (downtime + response)</li>
              </ul>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Deployment Thresholds</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-emerald-50 rounded-xl">
                <p className="text-2xl font-bold text-emerald-700 mb-1">&lt; 50,000 sq ft</p>
                <p className="text-sm text-slate-600 font-medium">Standard Coverage</p>
                <p className="text-xs text-slate-500 mt-1">Incident rate: 4% per 1,000 daily visitors</p>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-xl">
                <p className="text-2xl font-bold text-amber-700 mb-1">50K – 100K sq ft</p>
                <p className="text-sm text-slate-600 font-medium">Enhanced Coverage</p>
                <p className="text-xs text-slate-500 mt-1">Incident rate: 6% per 1,000 daily visitors</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-xl">
                <p className="text-2xl font-bold text-red-700 mb-1">&gt; 100,000 sq ft</p>
                <p className="text-sm text-slate-600 font-medium">Maximum Coverage</p>
                <p className="text-xs text-slate-500 mt-1">Incident rate: 8% per 1,000 daily visitors</p>
              </div>
            </div>
          </div>
          <div className="text-center mt-10">
            <Link to={`/${language}/operational-risk`} className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors" data-testid="link-to-risk-model">
              Explore the full Model Risk Coverage calculator <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-16" id="exposure-calculator" style={{ scrollMarginTop: '80px' }} data-testid="exposure-calculator-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
            <div className="bg-red-600 px-8 py-5 flex items-center gap-3 rounded-t-2xl">
              <DollarSign className="w-5 h-5 text-white" />
              <h3 className="text-white font-semibold">Exposure & Loss Calculator</h3>
            </div>
            <div className="p-8 md:p-10">
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Industry Type</label>
                  <select
                    value={calcIndustry}
                    onChange={e => setCalcIndustry(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                    data-testid="exposure-calc-industry"
                  >
                    <option value="">Select industry</option>
                    <option value="Retail Warehouse">Retail Warehouse</option>
                    <option value="Grocery Store">Grocery Store</option>
                    <option value="Airport">Airport</option>
                    <option value="Stadium">Stadium</option>
                    <option value="Hotel / Hospitality">Hotel / Hospitality</option>
                    <option value="Healthcare Facility">Healthcare Facility</option>
                    <option value="Restaurant Chain">Restaurant Chain</option>
                    <option value="Shopping Mall">Shopping Mall</option>
                    <option value="Casino / Gaming">Casino / Gaming</option>
                    <option value="Real Estate / Property">Real Estate / Property</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Facility Size (sq ft)</label>
                  <input
                    type="number"
                    value={calcSize}
                    onChange={e => setCalcSize(e.target.value)}
                    placeholder="50,000"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                    data-testid="exposure-calc-size"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Average Daily Visitors</label>
                  <input
                    type="number"
                    value={calcVisitors}
                    onChange={e => setCalcVisitors(e.target.value)}
                    placeholder="500"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                    data-testid="exposure-calc-visitors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Number of Locations</label>
                  <input
                    type="number"
                    value={calcLocations}
                    onChange={e => setCalcLocations(e.target.value)}
                    placeholder="1"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                    data-testid="exposure-calc-locations"
                  />
                </div>
              </div>
              <button
                onClick={calculateExposure}
                className="w-full md:w-auto px-8 py-3.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                data-testid="exposure-calculate-btn"
              >
                <Calculator className="w-5 h-5" />
                Calculate Exposure
              </button>

              {calcResults && (
                <div className="mt-8 grid md:grid-cols-3 gap-5" data-testid="exposure-calc-results">
                  <div className="bg-white rounded-xl p-6 border border-red-200 text-center">
                    <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
                    <p className="font-bold text-slate-900 mb-1 leading-tight" style={{ fontSize: `${Math.max(16, Math.min(28, 28 - Math.max(0, String(calcResults.incidents.toLocaleString()).length - 6) * 2))}px` }}>{calcResults.incidents.toLocaleString()}</p>
                    <p className="text-sm text-slate-600">Estimated Incidents / Year</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-amber-200 text-center">
                    <DollarSign className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                    <p className="font-bold text-slate-900 mb-1 leading-tight" style={{ fontSize: `${Math.max(14, Math.min(28, 28 - Math.max(0, ('$' + calcResults.liability.toLocaleString()).length - 6) * 1.5))}px` }}>${calcResults.liability.toLocaleString()}</p>
                    <p className="text-sm text-slate-600">Estimated Liability Exposure</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-orange-200 text-center">
                    <Clock className="w-8 h-8 text-[#FF7F00] mx-auto mb-3" />
                    <p className="font-bold text-slate-900 mb-1 leading-tight" style={{ fontSize: `${Math.max(14, Math.min(28, 28 - Math.max(0, ('$' + calcResults.disruption.toLocaleString()).length - 6) * 1.5))}px` }}>${calcResults.disruption.toLocaleString()}</p>
                    <p className="text-sm text-slate-600">Estimated Operational Disruption</p>
                  </div>
                </div>
              )}
              <p className="text-xs text-slate-400 mt-6">Estimates based on industry averages and published research. Actual exposure may vary based on operational conditions, safety protocols, and facility management practices.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Context section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <TrendingUp className="w-10 h-10 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-900 mb-2">Incident Modeling</h3>
              <p className="text-sm text-slate-600">Hazard frequency modeled against foot traffic, facility size, and industry benchmarks.</p>
            </div>
            <div className="text-center">
              <DollarSign className="w-10 h-10 text-amber-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-900 mb-2">Liability Exposure</h3>
              <p className="text-sm text-slate-600">Average claim costs and litigation rates applied to estimated incident volume.</p>
            </div>
            <div className="text-center">
              <Shield className="w-10 h-10 text-[#FF7F00] mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-900 mb-2">Operational Disruption</h3>
              <p className="text-sm text-slate-600">Downtime and response costs factored per incident across your facility network.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExposureCalculatorPage;
